import axios, {
    AxiosError,
    AxiosHeaders,
    AxiosResponse,
    InternalAxiosRequestConfig,
    RawAxiosRequestHeaders,
} from "axios";


interface ApiErrorResponse {
    message?: string;
}

const forceLogoutAndRedirect = () => {
    if (typeof window === 'undefined' || !window.localStorage) return;

    try {
        window.localStorage.setItem('signingOut', 'true');
    } catch {
        // ignore
    }

    try {
        window.localStorage.removeItem('access_token');
    } catch {
        // ignore
    }

    // Avoid redirect loops if we're already on the login page.
    try {
        if (window.location.pathname !== '/login') {
            window.location.replace('/login');
        }
    } catch {
        // ignore
    }

    // Remove flag after navigation attempt.
    setTimeout(() => {
        try {
            window.localStorage.removeItem('signingOut');
        } catch {
            // ignore
        }
    }, 0);
};

// Clean the environment variable by removing quotes, escaped quotes, and whitespace
const rawBackendUrl = import.meta.env.VITE_BACKEND_URL || '';
const cleanBackendUrl = rawBackendUrl
  .replace(/^["']|["']$/g, '')  // Remove quotes from start/end
  .replace(/\\"/g, '')          // Remove escaped quotes \"
  .replace(/\\'/g, '')          // Remove escaped single quotes \'
  .replace(/\\\\/g, '')         // Remove escaped backslashes \\
  .trim();                      // Remove whitespace


// Ensure we always have a proper baseURL in production
export const finalBaseURL = import.meta.env.PROD
  ? (cleanBackendUrl ? `${cleanBackendUrl}/api/v1` : 'https://twillo-estrabilio-staging.optimusai.ai/api/v1') // fallback if env var is empty
  : '';



const axiosInstance = axios.create({
    baseURL: finalBaseURL,
    withCredentials: false, // Change to false for CORS
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    // Add timeout
    timeout: 30000,
});



const onRequest = async (
    config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
    const token =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('access_token')
        : null;

        if (token && token !== 'undefined') {
        const authToken = `Bearer ${token}`;

        if (!config.headers) {
            config.headers = new AxiosHeaders();
        }

        if (config.headers instanceof AxiosHeaders) {
            config.headers.set('Authorization', authToken);
        } else {
            (config.headers as RawAxiosRequestHeaders).Authorization = authToken;
        }
    }

    // Log the full URL being requested
    // const fullURL = (config.baseURL || '') + (config.url || '');
    // console.log('🚀 Axios Request:', {
    //     method: config.method?.toUpperCase(),
    //     baseURL: config.baseURL,
    //     url: config.url,
    //     fullURL: fullURL
    // });
    return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
};
  
const onResponse = (response: AxiosResponse): AxiosResponse => {
    return response;
};
  
const onResponseError = async (error: AxiosError<ApiErrorResponse>) => {
    const errorMessage =
        error?.response?.data?.message || error?.message || "An error occured";
    const signingOut = typeof window !== 'undefined' && 
    window.localStorage && 
    window.localStorage.getItem('signingOut') === 'true';

    const status = error.response?.status;
    const requestUrl = (error.config?.url || '').toString();
    const isMeRequest = requestUrl.includes('/auth/me');
    const isLoginOrLogoutRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/logout');

    if (!signingOut && (status === 401) && !isLoginOrLogoutRequest) {
        if (isMeRequest || status === 401) {
            forceLogoutAndRedirect();
        }
    }

    if (
        signingOut ||
        (errorMessage && errorMessage.toLowerCase().includes('unauthor'))
    ) {
        // Suppress unauthorized toast during signout
        return Promise.reject(error);
    }
    
  
    return Promise.reject(error);
};
  
axiosInstance.interceptors.request.use(onRequest, onRequestError);
axiosInstance.interceptors.response.use(onResponse, onResponseError);


export {
    axiosInstance
};